import React, { useState, useEffect, useRef } from 'react';
import Tool, { type ToolButtonComponent } from './Tool';
import ToolButton from '../toolbars/ToolButton';
import { ApplicationView } from '@aha-app/mvc';
import ColorSwatch, { ColorPalette, OpacitySlider } from './ColorSwatch';
import { colorPaletteForObject } from '../utils/DefaultsManager';
import { type Instance as TippyInstance } from 'tippy.js/index';
import { isGroupLike } from '../objects/guards';
import { enableLeafTheme } from './enableLeafTheme';

const Popup: React.FC<{
  currentColor: string;
  setCurrentColor: (color: string) => void;
  selection: fabric.Object;
  hidePopup: () => void;
  showBorderOpacitySilder: boolean;
  setCurrentOpacity: (opacity: number) => void;
}> = ApplicationView(
  ({
    currentColor,
    setCurrentColor,
    selection,
    hidePopup,
    showBorderOpacitySilder,
    setCurrentOpacity,
  }) => {
    const setStrokeColor = (color: string) => {
      setCurrentColor(color);

      selection.forEach(obj => {
        const colorWas = obj.stroke;
        obj.setOnObjects('stroke', color);
        // If the stroke was removed by being made transparent then offset the
        // object to keep the center in the same position.
        if (
          color !== colorWas &&
          (color === 'transparent' || colorWas === 'transparent')
        ) {
          const diff =
            color === 'transparent' ? obj.strokeWidth : -obj.strokeWidth;

          obj.set({
            top: obj.top + diff / 2,
            left: obj.left + diff / 2,
          });
        }
      });
      enableLeafTheme(selection, false);

      if (isGroupLike(selection)) selection.syncFromObjects();
    };

    const handleBorderOpacityChange = (borderOpacity: number) => {
      selection.setOnObjects('borderOpacity', borderOpacity);
      setCurrentOpacity(borderOpacity);
    };

    const ticks = '[2, 5, 10,20,30,40,50,60,70,80,90,100]';
    const borderOpacity = selection.firstObject().get('borderOpacity') || 1;

    return (
      <ColorPalette
        palette={colorPaletteForObject(selection.firstObject())}
        currentColor={currentColor}
        setCurrentColor={setStrokeColor}
        hidePopup={hidePopup}
      >
        {showBorderOpacitySilder && (
          <OpacitySlider
            ticks={ticks}
            opacity={borderOpacity}
            handleOpacityChange={handleBorderOpacityChange}
          />
        )}
      </ColorPalette>
    );
  }
);

const strokeColorButton = ({
  showBorderOpacitySilder = false,
  hollow = true,
}): ToolButtonComponent => {
  return ApplicationView(({ selection, tool }) => {
    const tippyRef = useRef<TippyInstance>();
    const obj = selection.firstObject();
    const strokeColor = obj.get('stroke');
    const opacity = obj.get('borderOpacity') ?? 1;
    const [currentColor, setCurrentColor] = useState(strokeColor);
    const [currentOpacity, setCurrentOpacity] = useState(opacity);

    // Update color if the props change.
    useEffect(() => {
      setCurrentColor(strokeColor);
    }, [strokeColor]);

    return (
      <ToolButton
        popup={
          <Popup
            currentColor={currentColor}
            setCurrentColor={setCurrentColor}
            selection={selection}
            hidePopup={() => tippyRef.current?.hide()}
            showBorderOpacitySilder={showBorderOpacitySilder}
            setCurrentOpacity={setCurrentOpacity}
          />
        }
        tippyRef={tippyRef}
        tool={tool}
      >
        <ColorSwatch
          color={currentColor}
          hollow={hollow}
          swatchIsInToolbar
          opacity={currentOpacity}
        />
      </ToolButton>
    );
  });
};

class ToolBorderColor extends Tool {
  static toolName = 'Border color';

  static buttonComponent() {
    return strokeColorButton({ showBorderOpacitySilder: false });
  }
}

class ToolBorderColorWithOpacity extends Tool {
  static toolName = 'Border color';

  static buttonComponent() {
    return strokeColorButton({ showBorderOpacitySilder: true });
  }
}

class ToolStrokeColor extends Tool {
  static toolName = 'Color';

  static buttonComponent() {
    return strokeColorButton({ showBorderOpacitySilder: false, hollow: false });
  }
}

export { ToolBorderColor, ToolBorderColorWithOpacity, ToolStrokeColor };
